// Keeps an in-progress job application (cover letter, screening answer, attached documents) in
// the browser so it survives the apply pop-up closing — a detour to the profile page, a reload,
// or coming back later. One draft per job, 7 days, cleared once the application is sent.
// Best-effort: blocked storage (some in-app browsers) just means no draft, never an error.

const PREFIX = 'bt_apply_draft_';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const str = (v, max) => (typeof v === 'string' ? v.slice(0, max) : '');

function cleanDocs(docs) {
  if (!Array.isArray(docs)) return [];
  return docs
    .filter((d) => d && typeof d === 'object' && typeof d.path === 'string' && d.path)
    .slice(0, 30)
    .map((d) => ({ name: str(d.name, 100), path: str(d.path, 300), filename: str(d.filename, 200) }));
}

export function saveApplyDraft(jobId, { cover, customAnswer, documents } = {}) {
  if (!jobId) return;
  try {
    const draft = { cover: str(cover, 5000), customAnswer: str(customAnswer, 2000), documents: cleanDocs(documents) };
    if (!draft.cover && !draft.customAnswer && draft.documents.length === 0) {
      localStorage.removeItem(PREFIX + jobId);
      return;
    }
    localStorage.setItem(PREFIX + jobId, JSON.stringify({ ...draft, at: Date.now() }));
  } catch (e) {
    /* storage blocked — no draft */
  }
}

export function loadApplyDraft(jobId) {
  if (!jobId) return null;
  try {
    const raw = localStorage.getItem(PREFIX + jobId);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || !d.at || Date.now() - d.at > MAX_AGE_MS) {
      localStorage.removeItem(PREFIX + jobId);
      return null;
    }
    return { cover: str(d.cover, 5000), customAnswer: str(d.customAnswer, 2000), documents: cleanDocs(d.documents) };
  } catch (e) {
    return null;
  }
}

export function clearApplyDraft(jobId) {
  if (!jobId) return;
  try {
    localStorage.removeItem(PREFIX + jobId);
  } catch (e) {
    /* ignore */
  }
}

// Loose match between an employer's document label and a saved file's label:
// "NBI Clearance" ~ "nbi clearance", "Resume" ~ "CV" / "Biodata".
const RESUME_WORDS = /\b(resume|resumé|cv|curriculum|biodata|bio-data)\b/i;
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export function isResumeDoc(name) {
  return RESUME_WORDS.test(String(name || ''));
}

export function findSavedMatch(docName, savedDocs) {
  if (!Array.isArray(savedDocs) || !savedDocs.length) return null;
  const n = norm(docName);
  if (!n) return null;
  const exact = savedDocs.find((d) => norm(d.name) === n);
  if (exact) return exact;
  if (isResumeDoc(docName)) return savedDocs.find((d) => isResumeDoc(d.name)) || null;
  return null;
}
